import React, { useCallback, useMemo } from 'react';
import merge from 'lodash/merge';

import { useTranslation } from '@lyearn/i18n';
import { VisibilitySettingsAccessTypeEnum } from '@/types/schema';

import RestrictedVisibilitySettingsContent from './components/RestrictedVisibilitySettingsContent';
import { RestrictedVisibilitySettingsContentProps } from './components/RestrictedVisibilitySettingsContent/types';
import {
  getGroupsRestrictedContentProps,
  getUsersAndGroupsRestrictedContentProps,
} from './components/RestrictedVisibilitySettingsContent/utils';
import { GroupModal, UsersAndGroupsModal } from './components/RestrictedVisibilitySettingsModal';
import RestrictedVisibilitySettingsSelectedList from './components/RestrictedVisibilitySettingsSelectedList';
import SettingsTabs from './components/VisibilitySettingsTabs';
import useVisibilitySettingsActions from './hooks/useVisibilitySettingsActions';
import {
  DEFAULT_OVERRIDES,
  getVisibilitySettingsTabs,
  SIDEBAR_MODE_DEFAULT_OVERRIDES,
} from './consts';
import { VisibilitySettingsFormFieldProps } from './types';

const VisibilitySettingsFormField: React.FC<VisibilitySettingsFormFieldProps> = React.memo(
  (props) => {
    const { t } = useTranslation(['Group', 'VisibilitySetting', 'Common']);
    const {
      type = 'groups',
      mode,
      value,
      disabled,
      isSettingsTabHidden,
      onChange,
      overrides: overridesProp,
      // renderModal,
      // renderSelectedItems,
      contextualGroupsConfig,
      allowGroupCsvImport,
    } = props;

    const isSidebarMode = mode === 'sidebar';
    const accessTypes = useMemo(() => getVisibilitySettingsTabs(t), [t]);
    const { onAccessTypeChange } = useVisibilitySettingsActions({ value, onChange });

    const overrides: VisibilitySettingsFormFieldProps['overrides'] = useMemo(
      () =>
        merge(isSidebarMode ? SIDEBAR_MODE_DEFAULT_OVERRIDES : DEFAULT_OVERRIDES, overridesProp),
      [overridesProp, isSidebarMode],
    );

    const restrictedContentProps = useMemo(
      () =>
        type === 'groups'
          ? getGroupsRestrictedContentProps({ t, props: overrides?.restrictedContent })
          : getUsersAndGroupsRestrictedContentProps({ t, props: overrides?.restrictedContent }),
      [overrides, t, type],
    );

    const renderModal: RestrictedVisibilitySettingsContentProps['renderModal'] = useCallback(
      (renderModalProps) => {
        const Component = type === 'groups' ? GroupModal : UsersAndGroupsModal;

        return (
          <Component
            {...renderModalProps}
            contextualGroupsConfig={contextualGroupsConfig}
            {...overrides?.modal}
          />
        );
      },
      [contextualGroupsConfig, overrides?.modal, type],
    );

    const renderSelectedItems: RestrictedVisibilitySettingsContentProps['renderSelectedItems'] =
      useCallback(
        (selectedListProps) => {
          return (
            <RestrictedVisibilitySettingsSelectedList
              {...selectedListProps}
              contextualGroupsConfig={contextualGroupsConfig}
              {...overrides?.selectedList}
            />
          );
        },
        [contextualGroupsConfig, overrides?.selectedList],
      );

    return (
      <>
        {disabled || isSettingsTabHidden ? null : (
          <SettingsTabs
            direction={isSidebarMode ? 'col' : 'row'}
            selectedTab={value.accessType}
            size={isSidebarMode ? 'medium' : 'large'}
            tabs={accessTypes}
            onTabChange={onAccessTypeChange}
          />
        )}
        {value.accessType === VisibilitySettingsAccessTypeEnum.Restricted ? (
          <RestrictedVisibilitySettingsContent
            allowGroupCsvImport={allowGroupCsvImport}
            disabled={disabled}
            isSettingsTabHidden={isSettingsTabHidden}
            renderModal={renderModal}
            renderSelectedItems={renderSelectedItems}
            value={value}
            onChange={onChange}
            {...restrictedContentProps}
          />
        ) : null}
      </>
    );
  },
);

export default VisibilitySettingsFormField;
